import { createSelector } from '@ngrx/store';

import { AppState } from '../root-reducer';

import { AdminActions, AdminActionTypes } from '../actions/admin.actions';

import { Account } from 'modules/shared/models/account.models';
import { AccountListResponse, GroupPermissionResponse } from '../models/admin.models';

export interface Error {
  action: AdminActionTypes;
  detail: string;
}

export interface State {
  allAccounts: AccountListResponse | null;
  accountsInCurrentDomain: AccountListResponse | null;
  permissions: GroupPermissionResponse | null;
  loading: Array<AdminActionTypes>;
  err: Error[] | [];
}

const initialState: State = {
  allAccounts: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },
  accountsInCurrentDomain: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },
  permissions: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },
  loading: [],
  err: [],
};

export function reducer(state: State = initialState, action: AdminActions): State {
  switch (action.type) {
    case AdminActionTypes.GET_ALL_ACCOUNTS_SUCCESS: {
      return {
        ...state,
        allAccounts: action.payload,
        loading: clearLoading(state, AdminActionTypes.GET_ALL_ACCOUNTS),
        err: clearError(state, AdminActionTypes.GET_ALL_ACCOUNTS)
      };
    }
    case AdminActionTypes.GET_ALL_ACCOUNTS_FAILURE: {
      return {
        ...state,
        allAccounts: {
          count: 0,
          next: null,
          previous: null,
          results: []
        },
        loading: clearLoading(state, AdminActionTypes.GET_ALL_ACCOUNTS),
        err: addError(state, AdminActionTypes.GET_ALL_ACCOUNTS, action.payload)
      };
    }
    case AdminActionTypes.GET_ACCOUNTS_IN_CURRENT_DOMAIN_SUCCESS: {
      return {
        ...state,
        accountsInCurrentDomain: action.payload,
        loading: clearLoading(state, AdminActionTypes.GET_ACCOUNTS_IN_CURRENT_DOMAIN),
        err: clearError(state, AdminActionTypes.GET_ACCOUNTS_IN_CURRENT_DOMAIN)
      };
    }
    case AdminActionTypes.GET_ACCOUNTS_IN_CURRENT_DOMAIN_FAILURE: {
      return {
        ...state,
        accountsInCurrentDomain: {
          count: 0,
          next: null,
          previous: null,
          results: []
        },
        loading: clearLoading(state, AdminActionTypes.GET_ACCOUNTS_IN_CURRENT_DOMAIN),
        err: addError(state, AdminActionTypes.GET_ACCOUNTS_IN_CURRENT_DOMAIN, action.payload)
      };
    }
    case AdminActionTypes.GET_PERMISSIONS_SUCCESS: {
      return {
        ...state,
        permissions: action.payload,
        loading: clearLoading(state, AdminActionTypes.GET_PERMISSIONS),
        err: clearError(state, AdminActionTypes.GET_PERMISSIONS)
      };
    }
    case AdminActionTypes.GET_PERMISSIONS_FAILURE: {
      return {
        ...state,
        permissions: {
          count: 0,
          next: null,
          previous: null,
          results: []
        },
        loading: clearLoading(state, AdminActionTypes.GET_PERMISSIONS),
        err: addError(state, AdminActionTypes.GET_PERMISSIONS, action.payload)
      };
    }
    case AdminActionTypes.CREATE_ACCOUNT_SUCCESS: {
      return {
        ...state,
        allAccounts: {
          ...state.allAccounts,
          results: [
            ...state.allAccounts.results,
            action.payload
          ]
        },
        loading: clearLoading(state, AdminActionTypes.CREATE_ACCOUNT),
        err: clearError(state, AdminActionTypes.CREATE_ACCOUNT)
      };
    }
    case AdminActionTypes.CREATE_ACCOUNT_FAILURE: {
      return {
        ...state,
        loading: clearLoading(state, AdminActionTypes.CREATE_ACCOUNT),
        err: addError(state, AdminActionTypes.CREATE_ACCOUNT, action.payload)
      };
    }
    case AdminActionTypes.UPDATE_ROLES_SUCCESS: {
      if (action.payload.oauthId) {
        const index = state.allAccounts.results.findIndex((acc: Account) => acc.oauthId === action.payload.oauthId);

          return {
            ...state,
            allAccounts: {
              ...state.allAccounts,
              results: [
                ...state.allAccounts.results.slice(0, index),
                {
                  ...state.allAccounts.results[index],
                  roles: action.payload.roles
                },
                ...state.allAccounts.results.slice(index + 1),
              ]
            },
            loading: clearLoading(state, AdminActionTypes.UPDATE_ROLES),
            err: clearError(state, AdminActionTypes.UPDATE_ROLES)
          };
      } else {
        return {
          ...state,
          loading: clearLoading(state, AdminActionTypes.UPDATE_ROLES),
          err: clearError(state, AdminActionTypes.UPDATE_ROLES)
        };
      }
    }
    case AdminActionTypes.UPDATE_ROLES_FAILURE: {
      return {
        ...state,
        allAccounts: state.allAccounts,
        loading: clearLoading(state, AdminActionTypes.UPDATE_ROLES),
        err: addError(state, AdminActionTypes.UPDATE_ROLES, action.payload)
      };
    }
    case AdminActionTypes.ACCOUNT_BATCH_UPLOAD_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, AdminActionTypes.ACCOUNT_BATCH_UPLOAD),
        err: clearError(state, AdminActionTypes.ACCOUNT_BATCH_UPLOAD)
      };
    }
    case AdminActionTypes.ACCOUNT_BATCH_UPLOAD_FAILURE: {
      return {
        ...state,
        loading: clearLoading(state, AdminActionTypes.ACCOUNT_BATCH_UPLOAD),
        err: addError(state, AdminActionTypes.ACCOUNT_BATCH_UPLOAD, action.payload)
      };
    }
    case AdminActionTypes.GET_ALL_ACCOUNTS:
    case AdminActionTypes.GET_ACCOUNTS_IN_CURRENT_DOMAIN:
    case AdminActionTypes.GET_PERMISSIONS:
    case AdminActionTypes.CREATE_ACCOUNT:
    case AdminActionTypes.UPDATE_ROLES:
    case AdminActionTypes.ACCOUNT_BATCH_UPLOAD: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    default: {
      return state;
    }
  }
}

const admin = (state: AppState) => state.admin;
export const Selectors = {
  getErrors: createSelector(admin, (state: State) => state.err),
  getLoading: createSelector(admin, (state: State) => state.loading),
  getAllAccounts: createSelector(admin, (state: State) => state.allAccounts),
  getAccountsInCurrentDomain: createSelector(admin, (state: State) => state.accountsInCurrentDomain),
  getPermissions: createSelector(admin, (state: State) => state.permissions),
};

function clearLoading(state: State, action: AdminActionTypes): AdminActionTypes[] {
  return state.loading.filter((l: AdminActionTypes) => l !== action);
}

function clearError(state: State, action: AdminActionTypes): Error[] {
  return state.err.filter((err: Error) => err.action !== action);
}

function addError(state: State, action: AdminActionTypes, detail: string): Error[] {
  return [...state.err, <Error>{
    action: action,
    detail: detail
  }];
}
